import React, { useState, useMemo } from 'react';
import { cn, fuzzyMatchVi } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Search } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { SearchableCombobox } from './SearchableCombobox';

export interface PageFilterTagItem {
    key: string;
    label: string;
    color?: string;
    dotColor?: string;
    count?: number;
    tone?: 'slate' | 'amber' | 'blue' | 'cyan' | 'emerald' | 'rose' | 'zinc' | 'purple' | 'indigo';
}

interface PageFilterTagsProps {
    items: PageFilterTagItem[];
    selectedKeys: string[] | null;
    onSelect: (key: string | null) => void;
    /** Hide items with 0 count */
    hideEmpty?: boolean;
    /** Label for the overflow/mobile dropdown button (default: "Trạng thái") */
    dropdownLabel?: string;
    /** Force display as a single select combobox on desktop */
    forceCombobox?: boolean;
}

/**
 * Tự động xác định tone màu sắc Semantic dựa trên key, label hoặc class color
 * Áp dụng 8 tone chuẩn EMS Enterprise:
 * 1. Slate: Tất cả / Mặc định
 * 2. Amber: Chờ duyệt / Báo giá nháp
 * 3. Blue: Đã duyệt / Ban hành / Xác nhận
 * 4. Cyan: Đang thực hiện / Đang SX / Đang in / Vận chuyển
 * 5. Emerald: Hoàn tất / Đã giao / Đã thu / KCS Đạt
 * 6. Rose: Cảnh báo / NCR / Trễ hạn / Lỗi / Từ chối
 * 7. Zinc: Hủy / Khóa / Đã xóa / Ngưng hoạt động
 * 8. Purple: Nội bộ / Gia công ngoài / Thầu phụ
 */
function resolveSemanticTone(item: PageFilterTagItem): {
    inactiveClass: string;
    activeClass: string;
    badgeInactiveClass: string;
    badgeActiveClass: string;
    dotClass: string;
} {
    if (item.tone) {
        return getToneStyles(item.tone);
    }

    const key = (item.key || '').toLowerCase();
    const label = (item.label || '').toLowerCase();
    const color = (item.color || '').toLowerCase();

    // 1. Success / Completed / Delivered / Settled / Passed (Emerald)
    if (
        key.includes('approved') ||
        key.includes('ready') ||
        key.includes('completed') ||
        key.includes('available') ||
        key.includes('passed') ||
        key.includes('success') ||
        key.includes('paid') ||
        label.includes('sẵn sàng') ||
        label.includes('đã duyệt') ||
        label.includes('hoàn tất') ||
        label.includes('đạt') ||
        label.includes('đã thu') ||
        label.includes('đã thanh toán') ||
        color.includes('emerald') ||
        color.includes('green')
    ) {
        return getToneStyles('emerald');
    }

    // 2. Danger / Error / Rejected / Overdue / NCR / Scrap (Rose)
    if (
        key.includes('reject') ||
        key.includes('fail') ||
        key.includes('broken') ||
        key.includes('scrap') ||
        key.includes('ncr') ||
        key.includes('overdue') ||
        key.includes('error') ||
        key.includes('urgent') ||
        label.includes('từ chối') ||
        label.includes('không đạt') ||
        label.includes('hỏng') ||
        label.includes('lỗi') ||
        label.includes('trễ hạn') ||
        label.includes('quá hạn') ||
        label.includes('phế phẩm') ||
        label.includes('cảnh báo') ||
        color.includes('rose') ||
        color.includes('red')
    ) {
        return getToneStyles('rose');
    }

    // 3. Cancelled / Locked / Closed / Inactive (Zinc)
    if (
        key.includes('cancel') ||
        key.includes('locked') ||
        key.includes('closed') ||
        key.includes('inactive') ||
        key.includes('void') ||
        label.includes('hủy') ||
        label.includes('huỷ') ||
        label.includes('khóa') ||
        label.includes('đóng') ||
        label.includes('ngưng') ||
        color.includes('zinc') ||
        color.includes('gray')
    ) {
        return getToneStyles('zinc');
    }

    // 4. Outsource / Subcontract / Internal (Purple)
    if (
        key.includes('outsource') ||
        key.includes('subcontract') ||
        key.includes('internal') ||
        label.includes('gia công') ||
        label.includes('thầu phụ') ||
        label.includes('ngoài') ||
        label.includes('nội bộ') ||
        color.includes('purple') ||
        color.includes('fuchsia')
    ) {
        return getToneStyles('purple');
    }

    // 5. In Progress / Production / Printing / In Transit (Cyan)
    if (
        key.includes('run') ||
        key.includes('process') ||
        key.includes('transit') ||
        key.includes('shipping') ||
        key.includes('printing') ||
        key.includes('producing') ||
        key.includes('in_progress') ||
        label.includes('đang') ||
        label.includes('sản xuất') ||
        label.includes('in ấn') ||
        label.includes('cán màng') ||
        label.includes('bế') ||
        label.includes('dán') ||
        label.includes('vận chuyển') ||
        label.includes('giao hàng') ||
        color.includes('cyan') ||
        color.includes('teal')
    ) {
        return getToneStyles('cyan');
    }

    // 6. Confirmed / Released / Verified / Planned (Blue)
    if (
        key.includes('confirm') ||
        key.includes('release') ||
        key.includes('plan') ||
        key.includes('active') ||
        label.includes('ban hành') ||
        label.includes('xác nhận') ||
        label.includes('kế hoạch') ||
        label.includes('hoạt động') ||
        color.includes('blue') ||
        color.includes('sky')
    ) {
        return getToneStyles('blue');
    }

    // 7. Warning / Pending / Draft / Reviewing (Amber)
    if (
        key.includes('pending') ||
        key.includes('draft') ||
        key.includes('review') ||
        key.includes('warn') ||
        key.includes('new') ||
        label.includes('chờ') ||
        label.includes('nháp') ||
        label.includes('mới') ||
        label.includes('xem xét') ||
        color.includes('amber') ||
        color.includes('orange') ||
        color.includes('yellow')
    ) {
        return getToneStyles('amber');
    }

    // Default: Slate
    return getToneStyles('slate');
}

function getToneStyles(tone: string) {
    switch (tone) {
        case 'emerald':
            return {
                inactiveClass: 'bg-emerald-50 text-emerald-900 border-emerald-300 font-medium hover:bg-emerald-100/90',
                activeClass: 'bg-emerald-600 border-emerald-600 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-emerald-100 text-emerald-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-emerald-500',
            };
        case 'amber':
            return {
                inactiveClass: 'bg-amber-50 text-amber-900 border-amber-300 font-medium hover:bg-amber-100/90',
                activeClass: 'bg-amber-600 border-amber-600 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-amber-100 text-amber-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-amber-500',
            };
        case 'blue':
            return {
                inactiveClass: 'bg-blue-50 text-blue-900 border-blue-300 font-medium hover:bg-blue-100/90',
                activeClass: 'bg-blue-600 border-blue-600 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-blue-100 text-blue-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-blue-500',
            };
        case 'cyan':
            return {
                inactiveClass: 'bg-cyan-50 text-cyan-900 border-cyan-300 font-medium hover:bg-cyan-100/90',
                activeClass: 'bg-cyan-600 border-cyan-600 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-cyan-100 text-cyan-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-cyan-500',
            };
        case 'purple':
            return {
                inactiveClass: 'bg-purple-50 text-purple-900 border-purple-300 font-medium hover:bg-purple-100/90',
                activeClass: 'bg-purple-600 border-purple-600 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-purple-100 text-purple-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-purple-500',
            };
        case 'rose':
            return {
                inactiveClass: 'bg-rose-50 text-rose-900 border-rose-300 font-medium hover:bg-rose-100/90',
                activeClass: 'bg-rose-600 border-rose-600 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-rose-100 text-rose-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-rose-500',
            };
        case 'zinc':
            return {
                inactiveClass: 'bg-zinc-100 text-zinc-900 border-zinc-300 font-medium hover:bg-zinc-200/90',
                activeClass: 'bg-zinc-700 border-zinc-700 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-zinc-200 text-zinc-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-zinc-500',
            };
        case 'indigo':
            return {
                inactiveClass: 'bg-indigo-50 text-indigo-900 border-indigo-300 font-medium hover:bg-indigo-100/90',
                activeClass: 'bg-indigo-600 border-indigo-600 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-indigo-100 text-indigo-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-indigo-500',
            };
        case 'slate':
        default:
            return {
                inactiveClass: 'bg-slate-100 text-slate-900 border-slate-300 font-medium hover:bg-slate-200/90',
                activeClass: 'bg-slate-900 border-slate-900 text-white font-bold shadow-xs',
                badgeInactiveClass: 'bg-slate-200 text-slate-900',
                badgeActiveClass: 'bg-white/25 text-white',
                dotClass: 'bg-slate-500',
            };
    }
}

export function PageFilterTags({
    items,
    selectedKeys,
    onSelect,
    hideEmpty = false,
    dropdownLabel = 'Trạng thái',
    forceCombobox = false,
}: PageFilterTagsProps) {
    // Mục "Tất cả" do trang tự truyền (key ALL/all/'') trùng với chip "Tất cả" sẵn có → bỏ, coi như chọn tất cả
    const isAllKey = (k: unknown) => ['', 'all', 'ALL'].includes(String(k ?? ''));
    const isAllSelected = !selectedKeys || selectedKeys.length === 0 || selectedKeys.every(isAllKey);
    const [searchTerm, setSearchTerm] = useState('');

    const activeItems = (hideEmpty
        ? items.filter(item => (item.count || 0) > 0 || selectedKeys?.some(k => String(k) === String(item.key)))
        : items
    ).filter(item => !isAllKey(item.key));
    // Chuẩn lọc nhanh: dưới 5 lựa chọn → tag màu (mọi màn hình); từ 5 trở lên → danh sách thả xuống cho gọn
    const asDropdown = forceCombobox || activeItems.length >= 5;

    const filteredDropdownItems = useMemo(() => {
        if (!searchTerm) return activeItems;
        return activeItems.filter(item => fuzzyMatchVi(item.label, searchTerm));
    }, [activeItems, searchTerm]);

    if (activeItems.length === 0 && isAllSelected) return null;

    return (
        <div className="flex items-center gap-1.5 flex-nowrap shrink-0 max-w-full">
            {forceCombobox ? (
                <div className="hidden md:flex w-[200px] shrink-0">
                    <SearchableCombobox
                        options={activeItems.map(item => ({
                            value: item.key,
                            label: item.count !== undefined ? `${item.label} (${item.count})` : item.label,
                        }))}
                        value={selectedKeys?.[0] || ''}
                        onChange={(v) => onSelect(v || null)}
                        placeholder={dropdownLabel}
                        clearable
                    />
                </div>
            ) : !asDropdown ? (
                <ScrollArea className="w-full whitespace-nowrap">
                    <div className="flex items-center gap-1.5 flex-nowrap shrink-0 py-0.5">
                        {/* Chip "Tất cả" */}
                        <button
                            type="button"
                            onClick={() => onSelect(null)}
                            className={cn(
                                'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-all whitespace-nowrap border shrink-0 cursor-pointer',
                                isAllSelected
                                    ? 'bg-slate-900 border-slate-900 text-white font-bold shadow-xs'
                                    : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200/80 font-medium'
                            )}
                        >
                            <span>Tất cả</span>
                        </button>

                        {/* Danh sách Semantic Filter Chips */}
                        {activeItems.map((item) => {
                            const isSelected = selectedKeys?.some(k => String(k) === String(item.key));
                            const styles = resolveSemanticTone(item);

                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => onSelect(item.key)}
                                    className={cn(
                                        'group flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-all whitespace-nowrap border shrink-0 cursor-pointer',
                                        isSelected
                                            ? styles.activeClass
                                            : styles.inactiveClass
                                    )}
                                >
                                    {/* Dot Indicator */}
                                    <span
                                        className={cn(
                                            'w-1.5 h-1.5 rounded-full shrink-0 transition-opacity',
                                            isSelected ? 'bg-white' : styles.dotClass
                                        )}
                                    />
                                    <span>{item.label}</span>
                                    {item.count !== undefined && (
                                        <span className={cn(
                                            'inline-flex items-center justify-center min-w-[18px] h-[17px] px-1 rounded text-[10px] font-bold shrink-0 transition-colors',
                                            isSelected ? styles.badgeActiveClass : styles.badgeInactiveClass
                                        )}>
                                            {item.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <ScrollBar orientation="horizontal" className="h-1" />
                </ScrollArea>
            ) : null}

            {/* Từ 5 lựa chọn: danh sách thả xuống (forceCombobox: chỉ trên điện thoại, desktop đã có ô chọn) */}
            {asDropdown && (
            <div className={cn('flex shrink-0', forceCombobox && 'md:hidden')}>
                <DropdownMenu onOpenChange={(open) => !open && setSearchTerm('')}>
                    <DropdownMenuTrigger asChild>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-7 px-2.5 rounded-md text-xs font-medium gap-1.5 bg-white border-slate-200 shadow-2xs"
                        >
                            {(() => {
                                const selectedLabels = selectedKeys?.map(k => items.find(i => String(i.key) === String(k))?.label).filter(Boolean) || [];
                                if (selectedLabels.length === 1) return <><span className="text-slate-500 font-normal">{dropdownLabel}:</span> {selectedLabels[0]}</>;
                                if (selectedLabels.length > 1) return `${dropdownLabel} (${selectedLabels.length})`;
                                return dropdownLabel;
                            })()}
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 max-h-[320px] overflow-hidden flex flex-col z-50">
                        {activeItems.length >= 6 && (
                            <div className="p-2 border-b">
                                <div className="flex items-center px-2 py-1 bg-slate-100 rounded-md">
                                    <Search className="w-3.5 h-3.5 text-slate-400 mr-2 shrink-0" />
                                    <input
                                        className="bg-transparent text-xs w-full outline-none"
                                        placeholder={`Tìm ${dropdownLabel.toLowerCase()}...`}
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        onKeyDown={e => e.stopPropagation()}
                                    />
                                </div>
                            </div>
                        )}
                        <ScrollArea className="flex-1 overflow-y-auto">
                            <DropdownMenuCheckboxItem
                                checked={isAllSelected}
                                onCheckedChange={() => onSelect(null)}
                                className="text-xs font-medium mt-1"
                            >
                                Tất cả
                            </DropdownMenuCheckboxItem>
                            <DropdownMenuSeparator />
                            {filteredDropdownItems.length === 0 ? (
                                <div className="p-4 text-center text-xs text-slate-500">Không tìm thấy</div>
                            ) : (
                                filteredDropdownItems.map((item) => {
                                    const isSelected = selectedKeys?.some(k => String(k) === String(item.key));
                                    const styles = resolveSemanticTone(item);

                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={item.key}
                                            checked={isSelected}
                                            onCheckedChange={() => onSelect(item.key)}
                                            className="text-xs font-medium"
                                        >
                                            <div className="flex items-center justify-between w-full gap-2 py-0.5">
                                                <div className="flex items-center gap-2">
                                                    <span 
                                                        className={cn('w-2 h-2 rounded-full shrink-0', styles.dotClass)}
                                                    />
                                                    <span>{item.label}</span>
                                                </div>
                                                {item.count !== undefined && (
                                                    <Badge variant="secondary" className="px-1 text-[9px] h-4 bg-slate-100 shrink-0 font-bold">
                                                        {item.count || 0}
                                                    </Badge>
                                                )}
                                            </div>
                                        </DropdownMenuCheckboxItem>
                                    );
                                })
                            )}
                        </ScrollArea>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            )}
        </div>
    );
}
